// import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop, XYCoord } from 'react-dnd'
import * as React from "react";
import styled from "styled-components";
import { Card, Classes, Icon } from "@blueprintjs/core";
import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrag,
  useDrop,
  XYCoord,
} from "react-dnd";
import classNames from "classnames";
import { useDatatable2Context } from "../DataTable2";
import { ItemTypes } from "nsitools-react";
import { IDataTableColumn } from "./IDataTableColumn";

export interface IColumnCardProps {
  col: IDataTableColumn<any>;
  moveCard: (index: number, toIndex: number) => void;
  index: number;
  disabled: boolean;
}

const DraggableDiv = styled.div`
  &.hide-card {
    opacity: 0;
  }
`;

const StyledCard = styled(Card)`
  padding: 0.4rem 1rem !important;
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  & > .${Classes.CHECKBOX} {
    margin-bottom: 0 !important;
  }
  &.checked {
    background: rgba(191, 204, 214, 0.15);
  }
`;

const CardTextContainer = styled.div`
  & * + * {
    margin-left: 1rem;
  }
`;

export const ColumnCard: React.FunctionComponent<IColumnCardProps> = ({
  col,
  moveCard,
  index,
  disabled,
}) => {
  const {
    isColumnVisible,
    toggleColumnVisibility,
    renderColumnHeader,
    tableState,
  } = useDatatable2Context();

  const key = React.useMemo(() => col.fieldName, [col.fieldName]);
  const ref = React.useRef<HTMLDivElement>(null);

  const findCard = React.useCallback(
    (key: string) => {
      const foundCol = tableState.columnCustomizations.findIndex(
        (c) => c.fieldName === key
      );
      return {
        index: foundCol,
      };
    },
    [tableState.columnCustomizations]
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.CARD,
      item: { key, originalIndex: index },
      collect: (
        monitor: DragSourceMonitor<
          {
            key: string;
            originalIndex: number;
          },
          any
        >
      ) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (
        item: any,
        monitor: DragSourceMonitor<
          {
            key: string;
            originalIndex: number;
          },
          any
        >
      ) => {
        const { key: droppedKey, originalIndex } = item;
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          const { index: dragIndex } = findCard(droppedKey);
          moveCard(dragIndex, originalIndex);
        }
      },
    }),
    [key, index, moveCard]
  );

  const [{ handlerId }, drop] = useDrop(
    () => ({
      accept: ItemTypes.CARD,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item: any, monitor: DropTargetMonitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.originalIndex;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = ref.current?.getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        // Time to actually perform the action
        moveCard(dragIndex, hoverIndex);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.originalIndex = hoverIndex;
      },
    }),
    [findCard, moveCard]
  );

  React.useEffect(() => {
    drag(drop(ref));
  }, [drag, drop, ref]);

  return (
    <DraggableDiv
      key={`drag_${col.fieldName}`}
      ref={ref}
      data-handler-id={handlerId}
      className={classNames({
        "hide-card": isDragging,
      })}
      style={{
        cursor: disabled ? "not-allowed" : "default",
      }}
    >
      <StyledCard
        className={classNames({
          checked: !isColumnVisible(col),
        })}
        key={`card_${col.fieldName}`}
        interactive={true}
        onClick={() =>
          disabled ? null : toggleColumnVisibility(col, !isColumnVisible(col))
        }
      >
        <CardTextContainer>
          <Icon icon="drag-handle-horizontal" />
          <span>{renderColumnHeader(col)}</span>
        </CardTextContainer>
        {disabled ? null : (
          <Icon icon={isColumnVisible(col) ? "eye-open" : "eye-off"} />
        )}
      </StyledCard>
    </DraggableDiv>
  );
};
